import AddressIcons from '@/icons/AddressIcons'
import ClockIcons from '@/icons/ClockIcons'
import EmailIcons from '@/icons/EmailIcons'
import PhoneIcons from '@/icons/PhoneIcons'
import { Employee } from '@/modules/Employee/types/response'
import { formatDateWithFormatter } from '@/utils/function'
import React from 'react'
import { UseFormReturn } from 'react-hook-form'
import { Typogaphy } from 'vnetwork-ui'

interface ProfileHeaderProps {
    formControl: UseFormReturn<any>
    employee: Employee
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
    formControl,
    employee,
}) => {
    const getInitials = (name: string) => {
        return (
            name
                ?.split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2) || 'EM'
        )
    }

    return (
        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-3 md:p-4 mb-2">
            <div className="flex items-start justify-between min-w-0">
                <div className="flex items-center space-x-3 md:space-x-4 min-w-0 flex-1">
                    {/* Avatar */}
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-yellow-300 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-base md:text-xl font-semibold text-gray-800">
                            {getInitials(employee?.employee_name || '')}
                        </span>
                    </div>

                    <div className="min-w-0 flex-1">
                        <Typogaphy
                            size="large"
                            className="font-semibold text-gray-900 text-base md:text-lg truncate block"
                        >
                            {employee?.employee_name || 'N/A'}
                        </Typogaphy>
                        <div className="flex items-center gap-1 md:gap-2 mt-1 min-w-0">
                            <Typogaphy className="text-gray-600 text-xs md:text-sm truncate flex-shrink">
                                {employee?.department_name || 'N/A'}
                            </Typogaphy>
                            {employee?.position_name && (
                                <>
                                    <span className="text-gray-400 text-xs md:text-sm flex-shrink-0">
                                        •
                                    </span>
                                    <Typogaphy className="text-gray-600 text-xs md:text-sm truncate flex-shrink">
                                        {employee?.position_name}
                                    </Typogaphy>
                                </>
                            )}
                        </div>
                        <Typogaphy className="text-gray-500 text-xs md:text-sm mt-1 truncate block">
                            {employee?.employee_code || 'N/A'}
                        </Typogaphy>
                        {employee?.employee_email_company && (
                            <Typogaphy className="text-blue-600 text-xs md:text-sm mt-1 truncate block">
                                {employee?.employee_email_company}
                            </Typogaphy>
                        )}
                    </div>
                </div>
            </div>

            {/* Contact Information Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-200">
                <div className="flex items-center space-x-2 min-w-0">
                    <div className="w-3 h-3 md:w-4 md:h-4 text-gray-400 flex-shrink-0">
                        <PhoneIcons className="text-primary" />
                    </div>
                    <span className="text-gray-600 text-xs md:text-sm truncate block min-w-0">
                        {formControl?.watch('employee_phone_number') || 'N/A'}
                    </span>
                </div>

                <div className="flex items-center space-x-2 min-w-0">
                    <div className="w-3 h-3 md:w-4 md:h-4 text-gray-400 flex-shrink-0">
                        <EmailIcons className="text-primary" />
                    </div>
                    <span className="text-gray-600 text-xs md:text-sm truncate block min-w-0">
                        {formControl?.watch('employee_email') || 'N/A'}
                    </span>
                </div>

                <div className="flex items-center space-x-2 min-w-0">
                    <div className="w-3 h-3 md:w-4 md:h-4 text-gray-400 flex-shrink-0">
                        <AddressIcons className="text-primary" />
                    </div>
                    <span className="text-gray-600 text-xs md:text-sm truncate block min-w-0">
                        {formControl?.watch('employee_city') || 'N/A'}
                    </span>
                </div>

                <div className="flex items-center space-x-2 min-w-0">
                    <div className="w-3 h-3 md:w-4 md:h-4 text-gray-400 flex-shrink-0">
                        <ClockIcons className="text-primary" />
                    </div>
                    <span className="text-gray-600 text-xs md:text-sm truncate block min-w-0">
                        {formatDateWithFormatter(
                            employee?.employee_created_at,
                            'DD/MM/YYYY HH:mm'
                        )}
                    </span>
                </div>
            </div>
        </div>
    )
}

export default ProfileHeader
